import {
  DataRowType,
  InputDataType,
  QueryParams,
  Table,
  TextEllipsis,
  Tooltip,
  usePersistentTableState,
} from '@atlas-frontend/sdk';
import React, { ForwardedRef, forwardRef, useMemo } from 'react';
import {
  ColumnDef,
  ColumnFiltersState,
  createColumnHelper,
  OnChangeFn,
  Table as TanstackTable,
  SortingState,
} from '@tanstack/react-table';
import { path } from '../../../paths';
import { UiLink } from '../../../paths/UiLink';
import { makeInputDataFilterFn } from '../utils';
import { adjustColumns } from './utils';
import { InputDataCell } from './InputDataCell';

const columnHelper = createColumnHelper<DataRowType>();

export interface TableFilterSortProps {
  filters: ColumnFiltersState;
  onFilter: OnChangeFn<ColumnFiltersState>;
  sort: SortingState;
  onSort: OnChangeFn<SortingState>;
}

export interface InputDataTableProps {
  data?: InputDataType;
  date?: string;
  descriptionId?: number | string;
  groupId?: number | string;
  isLoading?: boolean;
  isDetailed?: boolean;
  subject?: string;
  outputDataVersionId?: number;
  stateStorageKey: string;
  tableProps?: TableFilterSortProps;
  subjectNameMappings?: Record<string, string>;
}

export const InputDataViewTable = forwardRef(
  (
    {
      data,
      date,
      descriptionId,
      groupId,
      outputDataVersionId,
      isLoading,
      isDetailed,
      stateStorageKey,
      tableProps,
      subjectNameMappings,
    }: InputDataTableProps,
    ref: ForwardedRef<TanstackTable<DataRowType>>,
  ) => {
    const { columns, rows } = useMemo(() => {
      if (!data) {
        return {
          columns: [] as ColumnDef<DataRowType, any>[],
          rows: [] as DataRowType[],
        };
      }
      const { labels, rows } = data;
      const columns = adjustColumns(data, isDetailed);

      const columnDefinitions: ColumnDef<DataRowType, any>[] = columns
        .map((column) => {
          if (column.hidden) {
            return null;
          }

          const header = () =>
            column.code === 'sequenceNumber' ? (
              column.name
            ) : (
              <Tooltip
                title={`${column.code} - ${column.name}`}
                placement="top"
              >
                {column.code}
              </Tooltip>
            );

          if (column.code === 'sequenceNumber') {
            return columnHelper.accessor(column.code, {
              header,
              cell: (props) => (
                <InputDataCell
                  code={column.code}
                  dataType={column.dataType}
                  labels={labels}
                  value={props.renderValue()}
                />
              ),
              meta: {
                width: getWidth(column.code),
                textAlign: 'right',
              },
              enableColumnFilter: false,
              enableSorting: false,
            });
          }

          if (column.code === 'SUBJEKT') {
            return columnHelper.accessor(column.code, {
              header,
              cell: (props) => {
                const subject = props.renderValue();
                const params: Partial<QueryParams> = {};

                if (descriptionId) params.descriptionId = descriptionId;
                if (date) params.date = date;
                if (subject) params.subject = subject;
                if (groupId) params.groupId = groupId;
                if (outputDataVersionId)
                  params.outputDataVersionId = outputDataVersionId;

                return (
                  <TextEllipsis>
                    <UiLink
                      id={`classifier-mapping-${props.row.index}`}
                      path={path.inputDataDetails}
                      params={params}
                    >
                      {subject +
                        (subject
                          ? ` - ${subjectNameMappings?.[subject] ?? ''}`
                          : '')}
                    </UiLink>
                  </TextEllipsis>
                );
              },
              filterFn: makeInputDataFilterFn(),
            });
          }

          return columnHelper.accessor(column.code, {
            header,
            cell: (props) => {
              const value = props.renderValue();
              return (
                <InputDataCell
                  code={column.code}
                  dataType={column.dataType}
                  labels={labels}
                  value={value}
                />
              );
            },
            meta: {
              width: getWidth(column.code),
              textAlign: column.type === 'number' ? 'right' : 'left',
            },
            filterFn: makeInputDataFilterFn(labels?.[column.code]),
            sortingFn: 'alphanumeric',
          });
        })
        .filter((col) => !!col) as ColumnDef<DataRowType, any>[];

      return { columns: columnDefinitions, rows };
    }, [
      data,
      date,
      descriptionId,
      groupId,
      isDetailed,
      outputDataVersionId,
      subjectNameMappings,
    ]);

    const { filters, setFilters, sort, setSort, pagination, setPagination } =
      usePersistentTableState(stateStorageKey);

    return (
      <Table
        name="input-data-table"
        data={rows}
        columns={columns}
        isLoading={isLoading}
        ref={ref}
        scrollContainerSelector="#detail-layout-main-content"
        sticky
        virtual
        manualFiltering={!!tableProps}
        manualSorting={!!tableProps}
        filters={tableProps?.filters ?? filters}
        onFilter={tableProps?.onFilter ?? setFilters}
        sort={tableProps?.sort ?? sort}
        onSort={tableProps?.onSort ?? setSort}
        pagination={tableProps ? undefined : pagination}
        onPagination={tableProps ? undefined : setPagination}
      />
    );
  },
);

const getWidth = (code: string) => {
  switch (code) {
    case 'sequenceNumber':
      return 110;
    case 'SUBJEKT':
      return 200;
    default:
      return undefined;
  }
};
